import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";
import * as Yup from "yup";
import CommonTable from "components/Common/TableContainer";
import Select from "react-select";
import {
    fetchSessionReportTeamOptions,
    submitSessionReportFilterData,
} from "model/SessionReportModel";

const sessionReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const SessionReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        teamId: "",
    });
    const [teamOptions, setTeamOptions] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [filterErrors, setFilterErrors] = useState({});

    const columns = [
        { Header: "S.N.", accessor: "sn" },
        { Header: "User", accessor: "user" },
        { Header: "Email", accessor: "email" },
        { Header: "Status", accessor: "status" },
        { Header: "No Of Days", accessor: "noOfDays" },
        { Header: "Avg Hours Logged in", accessor: "avgHoursLoggedIn" },
        { Header: "Avg Break Daily", accessor: "avgBreakDaily" },
    ];

    const filteredData = useMemo(() => reportData, [reportData]);

    const getSelectedOption = (options, value) =>
        options.find((opt) => String(opt?.value) === String(value)) || null;

    const getTeamOptions = async () => {
        const response = await fetchSessionReportTeamOptions();
        const responseData = response?.data ?? response ?? {};
        const records = Array.isArray(responseData)
            ? responseData
            : responseData?.records || responseData?.data || [];
        const options = (Array.isArray(records) ? records : [])
            .map((item) => {
                const value = String(item?.value ?? item?.id ?? item?._id ?? "").trim();
                const label = String(
                    item?.label ?? item?.display ?? item?.name ?? item?.teamName ?? value
                ).trim();
                if (!value) return null;
                return { value, label: label || value };
            })
            .filter(Boolean);
        setTeamOptions(options);
    };

    useEffect(() => {
        getTeamOptions();
    }, []);

    const handleFilterFieldChange = (e) => {
        const { name, value } = e.target;
        setFilterForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const handleTeamChange = (option) => {
        setFilterForm((prev) => ({
            ...prev,
            teamId: option?.value || "",
        }));
    };

    const getSessionReportList = async () => {
        try {
            await sessionReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        const payload = {
            dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
            dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
            teamId: filterForm?.teamId || null,
        };
        const response = await submitSessionReportFilterData(payload);
        const responseData = response?.data || {};
        const records = Array.isArray(responseData?.data)
            ? responseData?.data
            : Array.isArray(responseData?.records)
                ? responseData?.records
                : Array.isArray(responseData)
                    ? responseData
                    : [];
        const mappedData = (Array.isArray(records) ? records : []).map((item, idx) => ({
            ...item,
            sn: idx + 1,
            user: item?.userName || item?.user || item?.name || "",
            email: item?.userEmail || item?.email || "",
            status:
                String(item?.onlineHtml || item?.status || "").toLowerCase().includes("online")
                    ? "online"
                    : "offline",
            noOfDays: item?.nofDays ?? item?.noOfDays ?? item?.no_of_days ?? item?.days ?? "",
            avgHoursLoggedIn:
                item?.avgHoursLoggedIn || item?.average_hours_logged_in || item?.hoursLoggedIn || "-",
            avgBreakDaily: item?.avgBreakDaily || item?.average_break_daily || item?.breakDaily || "-",
        }));
        setReportData(mappedData);
    };

    return (
        <React.Fragment>
            <div className="page-content session-activity-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Session Activity Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end session-activity-filter-row w-100">
                                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                    <span className="manage-user-filter-label">From date</span>
                                    <Input
                                        type="date"
                                        name="fromDate"
                                        invalid={Boolean(filterErrors?.fromDate)}
                                        className={`${LEGACY_FILTER_INPUT_CLASS}${
                                            filterErrors?.fromDate ? " is-invalid" : ""
                                        }`}
                                        value={filterForm?.fromDate || ""}
                                        onChange={handleFilterFieldChange}
                                    />
                                    <FilterFieldFeedback message={filterErrors?.fromDate} />
                                </Col>
                                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                    <span className="manage-user-filter-label">To date</span>
                                    <Input
                                        type="date"
                                        name="toDate"
                                        invalid={Boolean(filterErrors?.toDate)}
                                        className={`${LEGACY_FILTER_INPUT_CLASS}${
                                            filterErrors?.toDate ? " is-invalid" : ""
                                        }`}
                                        value={filterForm?.toDate || ""}
                                        onChange={handleFilterFieldChange}
                                    />
                                    <FilterFieldFeedback message={filterErrors?.toDate} />
                                </Col>
                                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                    <span className="manage-user-filter-label">Team</span>
                                    <div className="w-100 session-activity-filter-select">
                                        <Select
                                            options={teamOptions}
                                            className="select2-selection"
                                            placeholder="Select Team"
                                            value={getSelectedOption(teamOptions, filterForm?.teamId)}
                                            onChange={handleTeamChange}
                                            isSearchable
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            menuPosition="fixed"
                                            styles={{
                                                container: (base) => ({ ...base, width: "100%" }),
                                                control: (base) => ({ ...base, width: "100%", minHeight: 38 }),
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    </div>
                                    <FilterFieldFeedback />
                                </Col>

                                <Col xs="12" sm="6" lg="3" className="session-activity-filter-col">
                                    <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                                    <Button
                                        color="primary"
                                        className="w-100 manage-user-search-btn"
                                        type="button"
                                        onClick={getSessionReportList}
                                    >
                                        <i className="fa fa-search me-1" aria-hidden="true" />
                                        Search
                                    </Button>
                                    <FilterFieldFeedback />
                                </Col>

                            </Row>
                            </LegacyFilterToolbar>

                            <CommonTable columns={columns} data={filteredData} isActionRequired={false} />
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

SessionReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(SessionReport);
